import {FC, useState} from 'react'
import {Link} from 'react-router-dom'
import {PreviewPdfData, PreviewPdfModal} from 'components/modals/PreviewPdfModal'
import {useQuery} from 'hooks/graphql'
import {dayOrTimeFormat} from 'libs/formatter'
import {FlatCard, FlatCardBody, FlatCardHeader, FlatCardListArrow, FlatCardListItem, FlatCardTitle} from '../FlatCard'
import {SeeMoreButton} from '../SeeMoreButton'
import {
  DASHBOARD_NOTIFICATIONS,
  DashboardNotificationsQueryVariables,
  DashboardNotificationsQueryResult,
  Notification,
  NotificationContent,
  FileUploadNotification,
} from './query'
import styles from './Notifications.scss'

const MAX_COUNT = 5

const isFileUploadNotification = (content: NotificationContent): content is FileUploadNotification => {
  return content.type === 'FileUploadNotification'
}

interface NotificationProps {
  notification: Notification
  setPreviewPdfData: (data: PreviewPdfData) => void
}

const NotificationItem: FC<NotificationProps> = ({notification, setPreviewPdfData}) => {
  const content = notification.notificationContent
  const dateOrTime = dayOrTimeFormat(content.createdAt)

  const showPreviewPdf = (content: FileUploadNotification) => {
    const {procedureStatusId, reportName, employeeDependentId, title} = content
    const url =
      `/api/procedure_statuses/${procedureStatusId}/pdf_to_be_uploaded?type=${reportName}` +
      (employeeDependentId ? `&employee_dependent_id=${employeeDependentId}` : '')
    setPreviewPdfData({
      title,
      url,
      pdf: url,
    })
  }

  const children = (
    <>
      <span className={styles.date}>{dateOrTime}</span>
      <span className={styles.title}>{content.title}</span>
      {content.link ? <FlatCardListArrow /> : null}
    </>
  )

  if (content.link) {
    if (content.link.startsWith('http')) {
      return (
        <FlatCardListItem as="a" href={content.link} target={content.targetBlank ? '_blank' : undefined}>
          {children}
        </FlatCardListItem>
      )
    } else {
      return (
        <FlatCardListItem as={Link} to={content.link} target={content.targetBlank ? '_blank' : undefined}>
          {children}
        </FlatCardListItem>
      )
    }
  } else if (isFileUploadNotification(content)) {
    return (
      <FlatCardListItem as="a" onClick={() => showPreviewPdf(content)}>
        {children}
      </FlatCardListItem>
    )
  } else {
    return <FlatCardListItem>{children}</FlatCardListItem>
  }
}

interface NotificationsProps {
  isAdmin: boolean
}

export const Notifications: FC<NotificationsProps> = ({isAdmin}) => {
  const [previewPdfData, setPreviewPdfData] = useState<PreviewPdfData>()
  const {data, loading} = useQuery<DashboardNotificationsQueryResult, DashboardNotificationsQueryVariables>(
    DASHBOARD_NOTIFICATIONS,
    {
      variables: {per: MAX_COUNT},
      fetchPolicy: 'network-only',
    }
  )
  const available = !loading && data
  const title = isAdmin ? '従業員の通知' : '管理者からの通知'

  return (
    <FlatCard>
      <PreviewPdfModal data={previewPdfData} onClose={() => setPreviewPdfData(undefined)} />
      <FlatCardHeader border>
        <FlatCardTitle>{title}</FlatCardTitle>
        <SeeMoreButton to={available && data.notifications.totalCount > 0 ? '/notifications' : undefined}>
          すべて見る
        </SeeMoreButton>
      </FlatCardHeader>
      <FlatCardBody className={styles.cardBody} cramp>
        {available &&
          (data.notifications.totalCount > 0 ? (
            data.notifications.list.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                setPreviewPdfData={setPreviewPdfData}
              />
            ))
          ) : (
            <FlatCardListItem className={styles.empty}>{title}はまだありません</FlatCardListItem>
          ))}
      </FlatCardBody>
    </FlatCard>
  )
}
